import React from "react";
import { useTranslation } from "react-i18next";
import "./SanitaryPostingDiagnostics.css";

export function postingFailureFromError(error, entry = {}) {
  const raw = error?.data || error?.response?.data;
  const body = raw && typeof raw === "object" ? raw : {};
  const outcomeUnknown = !body.code || body.outcome_unknown || body.code === "STOCK_POSTING_OUTCOME_UNKNOWN";
  return { id: entry.id, entry_id: entry.id, business_date: entry.business_date,
    product_name: entry.product?.name_public || entry.rule?.product?.name_public,
    location_id: entry.location_id, ok: false,
    outcome_unknown: Boolean(outcomeUnknown),
    code: outcomeUnknown ? "STOCK_POSTING_OUTCOME_UNKNOWN" : body.code,
    message: outcomeUnknown ? "Posting outcome was not confirmed. Refresh history before retrying." : body.message || error?.message || "",
    details: body.details || {}, persisted: body.persisted ?? body.posting_error_persisted };
}
export function PostingFailure({ failure, compact = false }) {
  const { t } = useTranslation(["sanitary", "stock"]);
  if (!failure?.code && !failure?.posting_error_code) return null;
  const code = failure.code || failure.posting_error_code;
  const message = failure.message || failure.posting_error_message || code;
  const details = failure.details || failure.posting_error_details_json || {};
  const at = failure.posting_error_at;
  return <div className={`sanitary-posting-failure${compact ? " sanitary-posting-failure--compact" : ""}`}>
    <strong>{t(code === "STOCK_POSTING_OUTCOME_UNKNOWN" ? "sanitary:postingDiagnostics.unknownTitle" : "sanitary:postingDiagnostics.failure")}</strong>
    <p>{t(`sanitary:postingDiagnostics.codes.${code}`, { ...details, defaultValue: t(`stock:postingSafety.codes.${code}`, { ...details, defaultValue: message }) })}</p>
    {details.required_quantity != null && <p>{t("sanitary:postingDiagnostics.quantities", {
      required: details.required_quantity, available: details.available_quantity ?? "—", unit: details.stock_unit_code || "",
    })}</p>}
    {at && <small>{t("sanitary:postingDiagnostics.lastAttempt", { at: new Date(at).toLocaleString() })}</small>}
    <details><summary>{t("sanitary:postingDiagnostics.technicalDetails")}</summary>
      <code>{code}</code>
      <p>{message}</p>
      {details.product_id && <p>{t("sanitary:postingDiagnostics.productLocation", { product: details.product_id, location: details.location_id || "—" })}</p>}
      {details.batch_id && <p>LOT #{details.batch_id}</p>}
      {Object.keys(details).length > 0 && <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify(details, null, 2)}</pre>}
      {failure.persisted === false && <p>{t("sanitary:postingDiagnostics.notPersisted")}</p>}
    </details>
  </div>;
}
export default function SanitaryPostingDiagnostics({ result, onDismiss }) {
  const { t } = useTranslation(["sanitary", "common"]);
  if (!result) return null;
  const failures = (Array.isArray(result.results) ? result.results : []).filter((r) => r.ok === false);
  const hasUnknownOutcome = failures.some((r) => r.outcome_unknown || r.code === "STOCK_POSTING_OUTCOME_UNKNOWN");
  return <section className={`sanitary-posting-report${failures.length ? " sanitary-posting-report--warning" : ""}`} aria-live="polite">
    <header><h2>{t("sanitary:postingDiagnostics.title")}{result.calculation_run_id ? ` · RUN #${result.calculation_run_id}` : ""}</h2>
      <button type="button" className="stock-md-button stock-md-button--secondary" onClick={onDismiss}>{t("common:actions.close")}</button></header>
    <p>{t(hasUnknownOutcome ? "sanitary:postingDiagnostics.unknownSummary" : "sanitary:postingDiagnostics.summary", { posted: result.posted || 0, requested: result.requested || 0,
      failed: result.failed || 0, blocked: result.blocked || 0, manual: result.skipped_solution_preparation || 0 })}</p>
    {result.has_more && <p><strong>{t("sanitary:postingDiagnostics.hasMore")}</strong></p>}
    {failures.length > 0 && <div className="stock-md-table-wrap"><table className="stock-md-table"><thead><tr>
      <th>{t("sanitary:postingDiagnostics.entry")}</th><th>{t("sanitary:postingDiagnostics.date")}</th>
      <th>{t("sanitary:postingDiagnostics.product")}</th><th>{t("sanitary:postingDiagnostics.reason")}</th>
    </tr></thead><tbody>{failures.map((row) => <tr key={row.id || row.entry_id}>
      <td>#{row.id || row.entry_id}</td><td>{row.business_date || "—"}</td><td>{row.product_name || (row.product_id ? `#${row.product_id}` : "—")}</td>
      <td><PostingFailure failure={row} compact /></td></tr>)}</tbody></table></div>}
    {failures.length > 0 && <p>{t("sanitary:postingDiagnostics.retryHint")}</p>}
  </section>;
}
